import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="mb-8">
              <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
                ← Back to Home
              </Link>
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
            <p className="text-gray-500 mb-8">Last updated: December 2025</p>

            <div className="prose prose-lg max-w-none text-gray-700">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p className="mb-4">
                  This Cookie Policy explains how QMAX Online mobile applications and website ("we", "our", "us") use 
                  cookies and similar technologies to recognize you when you visit our platforms. It explains what these 
                  technologies are and why we use them, as well as your rights to control our use of them.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. What Are Cookies?</h2>
                <p className="mb-4">
                  Cookies are small data files that are placed on your computer or mobile device when you visit a website 
                  or use an application. They are widely used to make websites and apps work efficiently and provide 
                  information to the owners.
                </p>
                <p className="mb-4">
                  For mobile applications, similar technologies include local storage, device identifiers, and 
                  analytics SDKs that serve comparable purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Technologies We Use</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Essential/Necessary Technologies</h3>
                <p className="mb-4">
                  These are required for the operation of our Apps and website. They include:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Authentication Tokens:</strong> Store your login session securely</li>
                  <li><strong>Session Data:</strong> Maintain your logged-in state across app sessions</li>
                  <li><strong>Security Features:</strong> Help detect and prevent unauthorized access</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Performance/Analytics Technologies</h3>
                <p className="mb-4">
                  These help us understand how users interact with our Apps:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Usage Analytics:</strong> Track which features are most popular</li>
                  <li><strong>Crash Reporting:</strong> Identify and fix technical issues</li>
                  <li><strong>Performance Monitoring:</strong> Ensure smooth app operation</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">3.3 Functional Technologies</h3>
                <p className="mb-4">
                  These enhance your experience by remembering your preferences:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Language Preferences:</strong> Remember your preferred language</li>
                  <li><strong>Display Settings:</strong> Store your view preferences</li>
                  <li><strong>Recent Searches:</strong> Quick access to previous tile searches</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Stored in Mobile Apps</h2>
                <p className="mb-4">
                  Our mobile applications may store the following data locally on your device:
                </p>
                <table className="w-full border-collapse border border-gray-300 mb-4">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Data Type</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Purpose</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Auth Token</td>
                      <td className="border border-gray-300 px-4 py-2">Keep you logged in</td>
                      <td className="border border-gray-300 px-4 py-2">Until logout</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">User Preferences</td>
                      <td className="border border-gray-300 px-4 py-2">Remember settings</td>
                      <td className="border border-gray-300 px-4 py-2">Persistent</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Cached Images</td>
                      <td className="border border-gray-300 px-4 py-2">Faster loading</td>
                      <td className="border border-gray-300 px-4 py-2">Auto-cleared</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Recent Searches</td>
                      <td className="border border-gray-300 px-4 py-2">Quick access</td>
                      <td className="border border-gray-300 px-4 py-2">30 days</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Cart/Wishlist</td>
                      <td className="border border-gray-300 px-4 py-2">Save selections</td>
                      <td className="border border-gray-300 px-4 py-2">Until cleared</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Services</h2>
                <p className="mb-4">
                  Our Apps may integrate with third-party services that use their own tracking technologies:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Google Play Services:</strong> For app distribution and updates (Android)</li>
                  <li><strong>Firebase:</strong> For crash reporting and analytics</li>
                  <li><strong>Push Notification Services:</strong> For sending notifications</li>
                </ul>
                <p className="mb-4">
                  These services have their own privacy policies, and we encourage you to review them.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Managing Your Preferences</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Mobile App Settings</h3>
                <p className="mb-4">You can control data storage in our apps by:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Clearing app cache through your device settings</li>
                  <li>Uninstalling the app to remove all stored data</li>
                  <li>Managing notification preferences within the app</li>
                  <li>Revoking app permissions in device settings</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Website Cookies</h3>
                <p className="mb-4">
                  For our website, you can manage cookies through your browser settings. Most browsers allow you to:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>View what cookies are stored</li>
                  <li>Delete cookies individually or all at once</li>
                  <li>Block cookies from all or specific websites</li>
                  <li>Set preferences for certain types of cookies</li>
                </ul>
                <p className="mb-4">
                  Please note that disabling essential cookies may affect the functionality of our services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Security</h2>
                <p className="mb-4">
                  All locally stored data is protected using industry-standard encryption methods. Authentication 
                  tokens are stored securely and transmitted only over encrypted connections (HTTPS).
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Updates to This Policy</h2>
                <p className="mb-4">
                  We may update this Cookie Policy from time to time to reflect changes in technology, legislation, 
                  or our data practices. We will notify you of any material changes by updating the "Last updated" 
                  date at the top of this policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Related Policies</h2>
                <p className="mb-4">
                  For more information about how we handle your data, please also review:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link> - How we collect and use your personal data</li>
                  <li><Link to="/terms-of-use" className="text-blue-600 hover:underline">Terms of Use</Link> - Terms governing use of our services</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
                <p className="mb-4">
                  If you have questions about our use of cookies or other technologies, please contact us:
                </p>
                <ul className="list-none space-y-2">
                  <li>📧 Email: <a href="mailto:info@starsolutions.biz" className="text-blue-600 hover:underline">info@starsolutions.biz</a></li>
                  <li>🌐 Website: <a href="https://qmaxonline.com" className="text-blue-600 hover:underline">qmaxonline.com</a></li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CookiePolicy;
